import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { ArtistForm } from 'NewVersion/components/Forms/ArtistForm'
import { ManagerForm } from 'NewVersion/components/Forms/ManagerForm'
import { ProposalForm } from 'NewVersion/components/Forms/ProposalForm'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import * as path from 'Routes/Paths'
import { PROPOSALS_GESTIO_PATH } from 'Routes/Paths'
import { useAddressForm, useGetVocabulariesProposalForm } from 'hooks'
import { useEdition } from 'hooks/useEdition'
import { useYears } from 'hooks/useYears'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router'
import { getPropostaGuillotina } from 'utils/apiCalls'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'
import { getCurrentJWToken } from 'utils/utils'

const CREATE_PROPOSAL_STEPS = {
  MANAGER: 0,
  ARTIST: 1,
  PROPOSAL: 2,
}

export const ProposalFormPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { idProposal, idArtist, year } = useParams()

  const { loading: loadingYears, years } = useYears()
  const { currentEdition, loading: loadingCurrentEdition } = useEdition()
  const [loadProposal, setLoadProposal] = useState(false)

  const {
    isLoaded: loadedVocabularies,
    countriesOptions,
    stateOptions,
    regionOptions,
  } = useAddressForm()

  const {
    isLoaded: loadedVocabulariesProposalForm,
    area,
    proposalType,
    features,
    audience,
    languages,
    release,
    keys,
    statement,
  } = useGetVocabulariesProposalForm()

  const searchParams = new URLSearchParams(location.search)
  const createProposalAsManager = searchParams.get('type') === ENS_TYPES_OBJ.representant.text
  const createProposalAsArtist = searchParams.get('type') === ENS_TYPES_OBJ.artista.text
  const preselectedArtistId = searchParams.get('artistId')
  const preselectedRepId = searchParams.get('repId')
  const createArtist = searchParams.get('createArtist') === 'true'
  const fromFlow = searchParams.get('from') // 'presentar' or 'gestio'

  function getCurrentStep() {
    // If we have preselected entities from dashboard
    if (preselectedArtistId && preselectedRepId) {
      // Manager flow with artist already selected - go to proposal
      return CREATE_PROPOSAL_STEPS.PROPOSAL
    } else if (preselectedArtistId && createProposalAsArtist) {
      // Artist flow with artist already selected - go to proposal
      return CREATE_PROPOSAL_STEPS.PROPOSAL
    } else if (preselectedRepId && createArtist) {
      // Manager wants to create new artist - go to artist form
      return CREATE_PROPOSAL_STEPS.ARTIST
    } else if (createProposalAsManager) {
      return CREATE_PROPOSAL_STEPS.MANAGER
    } else if (createProposalAsArtist) {
      return CREATE_PROPOSAL_STEPS.ARTIST
    } else if (idProposal && year) {
      return CREATE_PROPOSAL_STEPS.PROPOSAL
    }
    navigate('propostes')
  }

  const [state, setState] = useState({
    step: getCurrentStep(),
    representantObj: null,
    entitatsLoading: true,
    arrayAnys: [],
    representant: null, // representant que crea la proposta
    artista: null, // artista que té la proposta
    showDetailObj: null,
    propostaObj: null,
    editMode: null,
  })

  const isAllDataLoaded = React.useCallback(() => {
    return !!(
      loadedVocabulariesProposalForm() &&
      loadedVocabularies() &&
      !loadingYears &&
      !loadingCurrentEdition &&
      years
    )
  }, [
    loadedVocabulariesProposalForm,
    loadedVocabularies,
    loadingYears,
    years,
    loadingCurrentEdition,
  ])

  const setRepresentantObj = (representantV, representantObjV) => {
    setState((prev) => ({
      ...prev,
      representantObj: representantObjV,
      representant: representantV,
      step: CREATE_PROPOSAL_STEPS.ARTIST,
    }))
  }

  function setArtista(artista) {
    const moveToProposalStep = (nextArtista, nextRepresentantObj = undefined) => {
      setState((prev) => ({
        ...prev,
        artista: nextArtista,
        representantObj:
          nextRepresentantObj !== undefined ? nextRepresentantObj : prev.representantObj,
        step: CREATE_PROPOSAL_STEPS.PROPOSAL,
      }))
    }

    if (!state.representantObj && artista.representant) {
      const token = getCurrentJWToken()
      fetchGuillotina({
        path: artista.representant,
        method: 'GET',
        token,
      })
        .then((representantData) => {
          moveToProposalStep(artista, representantData)
        })
        .catch((error) => {
          console.error('Error fetching representant:', error)
          moveToProposalStep(artista)
        })
    } else {
      moveToProposalStep(artista)
    }
  }

  const toggleProposta = useCallback(
    (proposta = null) => {
      const normalizedProposal =
        proposta &&
        Object.assign({}, proposta, {
          ambits: proposta.ambits && area.filter((o) => o.value === proposta.ambits)[0],
          altres_ambits: getMultipleOptions(proposta.altres_ambits, area),
          tipus: proposta.tipus && proposalType.filter((e) => e.value === proposta.tipus)[0],
          caracteristiques:
            proposta.caracteristiques &&
            features.filter((e) => e.value === proposta.caracteristiques)[0],
          public: proposta.public && audience.filter((e) => e.value === proposta.public)[0],
          idioma: getMultipleOptions(proposta.idioma, languages),
          estrena: proposta.estrena && release.filter((e) => e.value === proposta.estrena)[0],
          claus: getMultipleOptions(proposta.claus, keys),
          declaracio_drets:
            proposta.declaracio_drets &&
            statement.filter((e) => e.value === proposta.declaracio_drets)[0],
          pais: getMultipleOptions(proposta.pais, countriesOptions),
          comunitat_autonoma: getMultipleOptions(proposta.comunitat_autonoma, stateOptions),
          comarca: getMultipleOptions(proposta.comarca, regionOptions),
        })

      setState((prev) => ({
        ...prev,
        step: CREATE_PROPOSAL_STEPS.PROPOSAL,
        ...(normalizedProposal ? { propostaObj: normalizedProposal } : {}),
      }))
    },
    [
      area,
      proposalType,
      features,
      audience,
      languages,
      release,
      keys,
      statement,
      countriesOptions,
      regionOptions,
      stateOptions,
    ]
  )

  // Load proposal from URL params
  useEffect(() => {
    if (
      idProposal &&
      state.step === CREATE_PROPOSAL_STEPS.PROPOSAL &&
      !loadProposal &&
      !state.propostaObj &&
      isAllDataLoaded()
    ) {
      setLoadProposal(true)
      getPropostaGuillotina(`${year}/${idArtist}/${idProposal}`, (data) => {
        toggleProposta(data)
        setLoadProposal(false)
      })
    }
  }, [
    idProposal,
    idArtist,
    state.step,
    loadProposal,
    state.propostaObj,
    toggleProposta,
    year,
    isAllDataLoaded,
  ])

  // Load preselected entities from query params (new flow)
  useEffect(() => {
    const loadPreselectedEntities = async () => {
      const token = getCurrentJWToken()

      // Load preselected representant
      if (preselectedRepId && !state.representantObj) {
        try {
          const repData = await fetchGuillotina({
            path: preselectedRepId,
            method: 'GET',
            token,
          })
          setState((prev) => ({ ...prev, representantObj: repData }))
        } catch (error) {
          console.error('Error loading preselected representant:', error)
        }
      }

      // Load preselected artist
      if (preselectedArtistId && !state.artista) {
        try {
          const artistData = await fetchGuillotina({
            path: preselectedArtistId,
            method: 'GET',
            token,
          })
          setState((prev) => ({ ...prev, artista: artistData }))
        } catch (error) {
          console.error('Error loading preselected artist:', error)
        }
      }
    }

    if (isAllDataLoaded() && (preselectedRepId || preselectedArtistId)) {
      loadPreselectedEntities()
    }
  }, [preselectedRepId, preselectedArtistId, isAllDataLoaded, state.representantObj, state.artista])

  function getMultipleOptions(data, values) {
    const arrayOptions = {
      name: '',
      value: [],
    }
    data &&
      data.forEach((v) => {
        const valuesFiltered = values.filter((o) => o.value === v)
        if (valuesFiltered.length > 0) {
          arrayOptions.value.push(valuesFiltered[0].value)
        }
      })
    return arrayOptions
  }

  function toggleInit() {
    // Return to the appropriate page based on context
    if (preselectedRepId && !createArtist) {
      // Came from representant dashboard
      const repIdClean = preselectedRepId.split('/').pop()
      navigate(path.PROPOSALS_DASHBOARD_REP_PATH.replace(':repId', repIdClean))
    } else if (preselectedArtistId && createProposalAsArtist) {
      // Came from artist dashboard
      const artistIdClean = preselectedArtistId.split('/').pop()
      navigate(path.PROPOSALS_DASHBOARD_ARTIST_PATH.replace(':artistId', artistIdClean))
    } else if (fromFlow === 'presentar') {
      navigate(path.PROPOSALS_PRESENTAR_PATH)
    } else {
      navigate(PROPOSALS_GESTIO_PATH)
    }
  }

  function handleGoBack() {
    if (fromFlow === 'presentar') {
      navigate(path.PROPOSALS_PRESENTAR_PATH)
    } else if (fromFlow === 'gestio') {
      navigate(PROPOSALS_GESTIO_PATH)
    } else {
      navigate(PROPOSALS_GESTIO_PATH)
    }
  }

  const renderManagerForm = () => {
    return (
      <>
        <ManagerForm
          setRepresentantObj={setRepresentantObj}
          showDetailObj={state.showDetailObj}
          toggleInit={toggleInit}
          ensTypeObj={ENS_TYPES_OBJ['representant']}
        />
      </>
    )
  }

  const renderArtistForm = () => {
    return (
      <>
        <ArtistForm
          representant={state.representantObj}
          setArtista={setArtista}
          ensTypeObj={ENS_TYPES_OBJ['artista']}
          toggleInit={toggleInit}
        />
      </>
    )
  }

  const renderProposalForm = () => {
    return (
      <>
        <ProposalForm
          artista={state.artista}
          showDetailObj={state.propostaObj}
          toggleInit={toggleInit}
          representant={state.representantObj}
          ensTypeObj={ENS_TYPES_OBJ['proposta']}
          years={years}
        />
      </>
    )
  }

  if (
    !isAllDataLoaded() ||
    (state.step === CREATE_PROPOSAL_STEPS.PROPOSAL && loadProposal && !state.propostaObj)
  )
    return <LoadingComponent />

  if (currentEdition.callIsFinished() && state.step !== CREATE_PROPOSAL_STEPS.PROPOSAL) {
    navigate(path.PROPOSALS_GESTIO_PATH)
  }

  const getBackButtonText = () => {
    if (fromFlow === 'presentar') {
      return t('back_to_presentar')
    } else if (fromFlow === 'gestio') {
      return t('back_to_gestio')
    }
    return t('backToProposals')
  }

  return (
    <div className="formPage page-wrapper">
      <GoBackButton onClick={handleGoBack} text={getBackButtonText()} />
      <div className="Shows Propostes">
        {state.step === CREATE_PROPOSAL_STEPS.MANAGER && renderManagerForm()}
        {state.step === CREATE_PROPOSAL_STEPS.ARTIST && renderArtistForm()}
        {state.step === CREATE_PROPOSAL_STEPS.PROPOSAL && renderProposalForm()}
      </div>
    </div>
  )
}

export default ProposalFormPage
